import { useState } from 'react';
import { useMe } from '../lib/me';
import { api } from '../lib/api';
import { useQuery } from '../lib/router';
import { Icon } from '../components/Icon';
import { Alert, Avatar, Badge, Button, Field, Modal, PageHeader, Panel, RoleChip, useAction } from '../components/ui';

export function ProfilePage() {
  const { me, refresh } = useMe();
  const q = useQuery();
  const { busy, run } = useAction();
  const [pwOpen, setPwOpen] = useState(false);
  if (!me) return null;
  const u = me.user;
  const linkErr = q.get('error');

  return (
    <div>
      <PageHeader eyebrow="Citizen" title="Profile & sign-in" />
      {q.get('linked') === 'google' && <Alert kind="ok">Google account linked. You can now sign in with Google.</Alert>}
      {linkErr && <Alert kind="danger">{linkErr === 'google_in_use' ? 'That Google account is already linked to another member.' : 'Linking failed. Please try again.'}</Alert>}

      <div className="grid-2">
        <Panel title="Account" icon="citizen">
          <div className="profile-id">
            <Avatar src={u.avatarUrl} name={u.displayName} size={64} />
            <div>
              <h2>{u.displayName}</h2>
              <div className="muted">@{u.username} {u.kind === 'local' && <Badge tone="gold">local account</Badge>}</div>
              {u.discordId && <div className="muted small mono">Discord ID {u.discordId}</div>}
            </div>
          </div>
          <h4 className="sub">Roles</h4>
          <div className="chips">
            {me.roles.length ? me.roles.map((r) => <RoleChip key={r.ref} name={r.name} color={r.color} title={r.ref.startsWith('local:') ? 'Local role' : 'Discord role'} />) : <span className="muted">No roles</span>}
          </div>
          <p className="muted small">Discord roles come from the Ravenbound Discord server and refresh every time you sign in.</p>
        </Panel>

        <Panel title="Sign-in methods" icon="key">
          <ul className="methods">
            <li>
              <Icon name="discord" size={22} />
              <div><strong>Discord</strong><span className="muted">{u.kind === 'discord' ? 'Primary sign-in' : 'Not used by local accounts'}</span></div>
              {u.kind === 'discord' ? <Badge tone="green">linked</Badge> : <Badge>n/a</Badge>}
            </li>
            <li>
              <Icon name="google" size={22} />
              <div><strong>Google</strong><span className="muted">{u.google ? u.google.email : me.providers.google ? 'Not linked' : 'Not configured on this server'}</span></div>
              {u.google
                ? <Button variant="ghost" busy={busy === 'unlink'} onClick={() => run('unlink', async () => { await api.post('/api/auth/google/unlink'); await refresh(); }, 'Google unlinked')}>Unlink</Button>
                : me.providers.google && <a className="btn btn-ghost" href="/api/auth/google/start?mode=link">Link</a>}
            </li>
            <li>
              <Icon name="key" size={22} />
              <div><strong>Password</strong><span className="muted">{u.passwordLogin ? `Login name: ${u.passwordLogin}` : 'Not set'}</span></div>
              <div className="row-actions">
                <Button variant="ghost" onClick={() => setPwOpen(true)}>{u.passwordLogin ? 'Change' : 'Set up'}</Button>
                {u.passwordLogin && u.kind === 'discord' && (
                  <Button variant="ghost" busy={busy === 'rmpw'} onClick={() => run('rmpw', async () => { await api.del('/api/auth/password'); await refresh(); }, 'Password sign-in removed')}>Remove</Button>
                )}
              </div>
            </li>
          </ul>
          <div className="panel-foot">
            <Button variant="ghost" icon="logout" busy={busy === 'all'} onClick={() => run('all', async () => { await api.post('/api/auth/logout-all'); location.href = '/login'; })}>Sign out everywhere</Button>
          </div>
        </Panel>
      </div>

      <Panel title="Your clearance" icon="clearance">
        {me.nav.length === 0 ? <p className="muted">No divisions unlocked yet.</p> : (
          <div className="clearance-summary">
            {me.nav.map((s) => (
              <div key={s.id}>
                <strong><Icon name={s.icon} size={15} /> {s.name}</strong>
                <ul>{s.tools.map((t) => <li key={t.id}>{t.name}</li>)}{s.tools.length === 0 && <li className="muted">Section only</li>}</ul>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {pwOpen && <PasswordModal hasPassword={!!u.passwordLogin} onClose={() => setPwOpen(false)} onDone={refresh} />}
    </div>
  );
}

function PasswordModal({ hasPassword, onClose, onDone }: { hasPassword: boolean; onClose: () => void; onDone: () => Promise<void> }) {
  const [login, setLogin] = useState('');
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const { busy, run } = useAction();
  const save = () => run('save', async () => {
    if (pw !== pw2) throw new Error('Passwords do not match');
    await api.post('/api/auth/password/set', { ...(hasPassword ? { currentPassword: current } : { login }), password: pw });
    await onDone();
    onClose();
  }, 'Password saved');
  return (
    <Modal title={hasPassword ? 'Change password' : 'Set up password sign-in'} onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy === 'save'} onClick={save}>Save</Button></>}>
      {!hasPassword && <Field label="Login name" hint="3–32 letters, numbers, dot, dash or underscore"><input autoFocus value={login} onChange={(e) => setLogin(e.target.value)} /></Field>}
      {hasPassword && <Field label="Current password"><input type="password" autoFocus value={current} onChange={(e) => setCurrent(e.target.value)} /></Field>}
      <Field label="New password" hint="At least 12 characters with letters and numbers"><input type="password" value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
      <Field label="Repeat new password"><input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></Field>
    </Modal>
  );
}
